
import OpenAI from 'openai';
import { marked } from 'marked';
import { supabase } from '@/lib/supabase';

export const getOpenAIClient = async () => {
  try {
    const { data, error } = await supabase.functions.invoke('get-openai-key', {
      method: 'GET',
    });

    if (error || !data?.apiKey) {
      console.error('Error getting OpenAI key from edge function:', error);
      throw new Error('Failed to retrieve OpenAI API key');
    }

    return new OpenAI({
      apiKey: data.apiKey,
      dangerouslyAllowBrowser: true
    });
  } catch (error) {
    console.error('Error getting OpenAI client:', error);
    throw error;
  }
};

export const generateEmailResponse = async (
  email: { subject: string; content?: string; preview: string },
  assistantId: string,
  userComment?: string
) => {
  console.log('Starting email response generation with:', {
    subject: email.subject,
    assistantId,
    hasUserComment: !!userComment
  });
  
  try {
    const openai = await getOpenAIClient();
    
    // First, get the assistant details to check if it has file search capabilities
    console.log('Getting assistant details:', assistantId);
    const assistant = await openai.beta.assistants.retrieve(assistantId);
    console.log('Assistant details:', {
      name: assistant.name,
      model: assistant.model,
      hasFileSearch: assistant.tools.some(t => t.type === 'file_search'),
      hasToolResources: !!assistant.tool_resources
    });

    // Create a thread
    console.log('Creating thread...');
    const thread = await openai.beta.threads.create();
    console.log('Thread created:', thread.id);

    // Add the email message to the thread
    console.log('Adding message to thread...');
    await openai.beta.threads.messages.create(
      thread.id,
      {
        role: 'user',
        content: `Respond to this email according to your instructions. You WILL only provide the email answer, nothing else:\n\nSubject: ${email.subject}\n\nContent: ${
          email.content || email.preview
        }${userComment ? `\n\nAdditional instructions: ${userComment}` : ''}`
      }
    );

    // Create run with proper configuration
    const runParams: any = {
      assistant_id: assistantId,
      model: assistant.model || undefined,
      instructions: assistant.instructions || undefined,
    };

    // Run the assistant
    console.log('Starting assistant run with ID:', assistantId);
    const run = await openai.beta.threads.runs.create(thread.id, runParams);
    console.log('Run created:', run.id);

    // Poll for completion with improved handling
    let response = null;
    let maxAttempts = 60; // 1 minute with 1-second intervals
    let attempts = 0;
    
    while (!response && attempts < maxAttempts) {
      console.log(`Checking run status... (Attempt ${attempts + 1}/${maxAttempts})`);
      const runStatus = await openai.beta.threads.runs.retrieve(
        thread.id,
        run.id
      );
      console.log('Current run status:', runStatus.status);
      
      if (runStatus.status === 'completed') {
        const messages = await openai.beta.threads.messages.list(thread.id);
        const assistantMessage = messages.data.find(
          (message) => message.role === 'assistant'
        );
        
        if (assistantMessage?.content?.[0]?.type === 'text') {
          response = assistantMessage.content[0].text.value;
          
          // Process any citations in the response
          const annotations = assistantMessage.content[0].text.annotations || [];
          if (annotations.length > 0) {
            console.log(`Found ${annotations.length} citations in the response`);
            response = await processCitations(openai, assistantMessage.content[0].text);
          }
          
          console.log('Response received from assistant');
        } else {
          console.error('No text content found in assistant response', assistantMessage);
          throw new Error('Assistant did not return any text content');
        }
      } else if (runStatus.status === 'failed') {
        console.error('Run failed:', runStatus.last_error);
        // Check for rate limit error specifically
        if (runStatus.last_error?.code === 'rate_limit_exceeded') {
          throw new Error('The OpenAI API rate limit has been exceeded. Please try again later or check your OpenAI account billing status.');
        } else {
          throw new Error(`Failed to generate response: ${runStatus.last_error?.message || 'Unknown error'}`);
        }
      } else if (runStatus.status === 'expired') {
        throw new Error('Run expired');
      } else if (runStatus.status === 'requires_action') {
        console.error('Run requires action:', runStatus.required_action);
        throw new Error('Run requires action that is not supported in this context');
      } else {
        // Wait before checking again
        await new Promise(resolve => setTimeout(resolve, 1000));
        attempts++;
      }
    }

    if (!response) {
      throw new Error(`Response generation timed out after ${maxAttempts} attempts`);
    }

    return response;
  } catch (error: any) {
    // Catch rate limit errors specifically
    if (error.message?.includes('rate_limit_exceeded') || 
        error.message?.includes('quota') || 
        error.message?.includes('exceeded your current')) {
      console.error('OpenAI rate limit or quota exceeded:', error);
      throw new Error('OpenAI API rate limit or quota exceeded. Please check your billing details or try again later.');
    }
    console.error('Error generating response:', error);
    throw error;
  }
};

// Helper function to process any citations in the response
export const processCitations = async (
  openai: OpenAI, 
  text: any
): Promise<string> => {
  if (!text.annotations || text.annotations.length === 0) {
    return text.value;
  }
  
  let processedText = text.value;
  const citations = [];
  
  for (let i = 0; i < text.annotations.length; i++) {
    const annotation = text.annotations[i];
    
    // Replace the citation text with a footnote
    processedText = processedText.replace(annotation.text, `[${i + 1}]`);
    
    // Get the file citation details if it exists
    if (annotation.file_citation) {
      try {
        const file = await openai.files.retrieve(annotation.file_citation.file_id);
        citations.push(`[${i + 1}] ${file.filename}`);
      } catch (error) {
        console.error('Error retrieving file for citation:', error);
        citations.push(`[${i + 1}] Reference to file`);
      }
    }
  }
  
  // Add citations at the end if there are any
  if (citations.length > 0) {
    processedText += '\n\n' + citations.join('\n');
  }
  
  return processedText;
};
